import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import {
  RadioGroup,
  NumberInput,
  Button,
  Icon,
} from 'uxd-design-system-components';
import './PersonalInfoForm.scss';
import StyledModal from '../StyledModal/StyledModal';
import getStatus from '../../utils/getStatus/getStatus';
import desanitizeString from '../../utils/sanitizeUtil/sanitizeUtil';
import { LocaleContext, PreviousPathContext } from '../../contexts';
import getText from '../../assets/locale/locale';
import addDataLayerTrigger from '../../utils/addDataLayerTrigger/addDataLayerTrigger';

const PersonalInfoForm = ({
  dispatch,
  answersDispatch,
  ACTIONS,
  answers,
  schema,
  setQuizStarted,
}) => {
  const history = useHistory();
  const location = useLocation();
  const locId = useContext(LocaleContext);
  const { setPreviousPath } = useContext(PreviousPathContext);

  const [localAge, setLocalAge] = useState('');
  const [localEmploymentStatus, setLocalEmploymentStatus] = useState(null);
  const [localRetirementAge, setLocalRetirementAge] = useState('');
  const [retirementAgeRange, setRetirementAgeRange] = useState({
    min: 18,
    max: 120,
  });
  const [ageError, setAgeError] = useState('');
  const [retAgeError, setRetAgeError] = useState('');

  const ageRange = {
    min: 18,
    max: 120,
  };
  const [empStatusOptionsArr, setEmpStatusOptionsArr] = useState([]);
  const [empStatSchema, setEmpStatSchema] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const ageLabel = getText(
    locId,
    'PersonalInfo',
    'ageLabel',
    'PersonalInfoForm'
  );
  const legend = getText(
    locId,
    'PersonalInfo',
    'radioLegend',
    'PersonalInfoForm'
  );

  const employmentStatusRetired = getText(
    locId,
    'PersonalInfo',
    'employmentStatusRetired',
    'PersonalInfoForm'
  );

  const { ageAnswer, statusAnswer, ret_ageAnswer } = answers; // eslint-disable-line camelcase
  const { employment_status } = schema; // eslint-disable-line camelcase

  let tooltips = [];

  // Remove hidden lable for Retirement Age Input Field from the DOM
  const removeHiddenRetirementAgeInputLabel = () => {
    const labelElements = document.getElementsByTagName('label');
    if (labelElements) {
      const retirementAgeLabelHidden = Array.from(labelElements).filter(
        (labelElement) => labelElement.innerText === 'ret_age_label_hidden'
      );
      if (retirementAgeLabelHidden && retirementAgeLabelHidden.length > 0) {
        retirementAgeLabelHidden[0].remove();
      }
    }
  };

  useEffect(() => {
    // filter the schema
    // eslint-disable-next-line camelcase
    const employmentStatus = employment_status;
    if (employmentStatus) {
      setEmpStatSchema(employmentStatus);
      const empStatusOptionsArrNew = [];
      employmentStatus.forEach((arrayValue) => {
        const newStatusValue = {
          id: arrayValue.value,
          label: desanitizeString(arrayValue.label),
          value: desanitizeString(arrayValue.label),
          checked: arrayValue.value === statusAnswer,
        };
        empStatusOptionsArrNew.push(newStatusValue);
      });
      setEmpStatusOptionsArr(empStatusOptionsArrNew);
    }
    tooltips = document.querySelectorAll('.tooltip-wrapper span');

    // eslint-disable-next-line no-use-before-define
    window.onmousemove = tooltipMouseOver;

    removeHiddenRetirementAgeInputLabel();
  }, []);

  useEffect(() => {
    // if the form was previously filled out, populate previous selections when mounting the component
    if (ageAnswer) {
      setLocalAge(ageAnswer);
    }
    if (statusAnswer) {
      setLocalEmploymentStatus(statusAnswer);
    }
    // eslint-disable-next-line camelcase
    if (ret_ageAnswer) {
      setLocalRetirementAge(Number(ret_ageAnswer));
    }
  }, []);

  useEffect(() => {
    if (localEmploymentStatus) {
      const newEmpStatusOptionsArr = [];
      empStatusOptionsArr.forEach((arrayValue) => {
        const isChecked = arrayValue.value === localEmploymentStatus;
        newEmpStatusOptionsArr.push({
          ...arrayValue,
          checked: isChecked,
        });
      });
      setEmpStatusOptionsArr(newEmpStatusOptionsArr);
    }
  }, [localEmploymentStatus]);

  useEffect(() => {
    // update retirement age range when employment status, age, or retirement age change
    if (localEmploymentStatus === employmentStatusRetired) {
      setRetirementAgeRange({
        min: 18,
        max: localAge || 120,
      });
    } else {
      setRetirementAgeRange({
        min: localAge || 18,
        max: 120,
      });
    }
  }, [localAge, localEmploymentStatus, localRetirementAge]);

  useEffect(() => {
    const marginLeft = retAgeError.length === 0 ? '232px' : '270px';
    const wrapper = document.querySelector('.ret-age-tooltip-wrapper');
    if (wrapper) wrapper.style.marginLeft = marginLeft;
  }, [retAgeError]);

  function tooltipMouseOver(e) {
    const x = `${e.clientX + 20}px`;
    const y = `${e.clientY + 20}px`;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < tooltips.length; i++) {
      tooltips[i].style.top = y;
      tooltips[i].style.left = x;
    }
  }

  const handleAgeOnChange = (e) => {
    if (e) {
      setLocalAge(Number(e));
    } else {
      setLocalAge('');
    }
  };

  const handleAgeOnBlur = (e) => {
    const errorMsg = () => {
      if (e) {
        if (
          Number(e) < ageRange.min ||
          Number(e) > ageRange.max ||
          !localRetirementAge
        ) {
          return Number(e) < ageRange.min || Number(e) > ageRange.max
            ? getText(
                locId,
                'PersonalInfo',
                'ageInstructions',
                'PersonalInfoForm'
              )
            : '';
        }
        if (
          localEmploymentStatus === employmentStatusRetired &&
          Number(e) < localRetirementAge
        ) {
          return getText(
            locId,
            'PersonalInfo',
            'retiredAgeGreaterThanAge',
            'PersonalInfoForm'
          );
        }
        if (
          localEmploymentStatus !== employmentStatusRetired &&
          Number(e) > localRetirementAge
        ) {
          return getText(
            locId,
            'PersonalInfo',
            'retiredAgeLesserThanAge',
            'PersonalInfoForm'
          );
        }
      }
      setRetAgeError('');
      return '';
    };
    setAgeError(errorMsg);
    addDataLayerTrigger('personal-age-field', { 'personal-age': e });
    handleAgeOnChange(e);
  };

  const handleEmpStatusChange = (e) => {
    const retErrorMsg = () => {
      if (localAge && localRetirementAge) {
        if (e === employmentStatusRetired && localAge < localRetirementAge) {
          return getText(
            locId,
            'PersonalInfo',
            'retiredAgeGreaterThanAge',
            'PersonalInfoForm'
          );
        }
        if (e !== employmentStatusRetired && localAge > localRetirementAge) {
          return getText(
            locId,
            'PersonalInfo',
            'retiredAgeLesserThanAge',
            'PersonalInfoForm'
          );
        }
      }
      setRetAgeError('');
      return '';
    };
    setAgeError(retErrorMsg);
    setLocalEmploymentStatus(e);
    addDataLayerTrigger('personal-radio-button', {
      'personal-employment-radio-selection': e,
    });
  };

  const handleRetAgeOnChange = (e) => {
    if (e) {
      setLocalRetirementAge(Number(e));
    } else {
      setLocalRetirementAge('');
    }
  };

  const handleRetAgeOnBlur = (e) => {
    const errorMsg = () => {
      if (e) {
        return Number(e) < retirementAgeRange.min ||
          Number(e) > retirementAgeRange.max
          ? `${getText(
              locId,
              'PersonalInfo',
              'ageErrorSentence',
              'PersonalInfoForm'
            ).replace('%MIN_AGE%', retirementAgeRange.min.toString())}
          ${getText(
            locId,
            'PersonalInfo',
            'ageErrorConjunction',
            'PersonalInfoForm'
          ).replace('%MAX_AGE%', retirementAgeRange.max.toString())}`
          : '';
      }
      setAgeError('');
      return '';
    };
    setRetAgeError(errorMsg);
    addDataLayerTrigger('personal-retirement-age-field', {
      'personal-retirement-age': e,
    });
    handleRetAgeOnChange(e);
  };

  const retPrompt = () => {
    if (localEmploymentStatus === employmentStatusRetired) {
      return getText(
        locId,
        'PersonalInfo',
        'retiredPrompt',
        'PersonalInfoForm'
      );
    }
    return getText(
      locId,
      'PersonalInfo',
      'willRetirePrompt',
      'PersonalInfoForm'
    );
  };

  const advance = () => {
    addDataLayerTrigger('advance-to-household');
    setQuizStarted(true);
    setPreviousPath(window.location.pathname);
    const localAgeOk = () => {
      if (localAge) {
        return localAge <= 120 && localAge >= 18;
      }
      return true;
    };

    const localRetirementAgeOk = () => {
      if (localRetirementAge) {
        return (
          localRetirementAge <= retirementAgeRange.max &&
          localRetirementAge >= retirementAgeRange.min
        );
      }
      return true;
    };

    if (localAgeOk() && localRetirementAgeOk()) {
      dispatch({
        type: ACTIONS.SET_PERSONAL_INFO,
        payload: {
          age: localAge,
          status: getStatus(localEmploymentStatus, empStatSchema),
          ret_age: localRetirementAge,
        },
      });
      answersDispatch({
        type: ACTIONS.SET_PERSONAL_INFO,
        payload: {
          age: localAge,
          status: localEmploymentStatus,
          ret_age: localRetirementAge,
        },
      });
      history.push({
        pathname: getText(locId, 'AllPages', 'householdPath', 'App'),
        search: location.search,
      });
    }
  };

  return (
    <div className="form-wrapper">
      <fieldset className="accessibility field-set">
        <legend>
          <span className="accessibility">
            {getText(locId, 'PersonalInfo', 'accessibilityFieldset')}
          </span>
        </legend>
        <div className="tooltip-wrapper">
          <NumberInput
            id="age"
            data-testid="age-input"
            attributes={{
              'aria-label': getText(
                locId,
                'PersonalInfo',
                'ageAriaLabel',
                'PersonalInfoForm'
              ),
            }}
            precision={0}
            rounded={false}
            label={ageLabel}
            name="age"
            value={localAge}
            onChange={handleAgeOnChange}
            onBlur={handleAgeOnBlur}
            error={ageError}
          />
          {!ageError ? (
            <span
              className="tooltip-wrapper--info-message"
              data-testid="tooltip-span"
            >
              {getText(
                locId,
                'PersonalInfo',
                'ageInstructions',
                'PersonalInfoForm'
              )}
            </span>
          ) : (
            <span className="tooltip-wrapper--info-message">{ageError}</span>
          )}
        </div>
        <RadioGroup
          legend={legend}
          name="employmentStatus"
          options={empStatusOptionsArr}
          onChange={handleEmpStatusChange}
        />
        <div className="ret-age-label">
          <label htmlFor="ret_age" className="input-control input-label">
            {retPrompt()}
          </label>
          <div
            role="button"
            aria-label={getText(
              locId,
              'PersonalInfo',
              'moreInfoAboutRetirementAgeAriaLabel',
              'PersonalInfoForm'
            )}
            className="why-ask-button ret-btn"
            onClick={() => setShowModal(true)}
            onKeyDown={(event) => {
              if (event.keyCode === 13 || event.keyCode === 32) {
                setShowModal(true);
              }
            }}
            tabIndex="0"
            data-testid="retirement-info"
          >
            <Icon name="notice-info" />
          </div>
        </div>
        <StyledModal
          headerText=""
          paragraphText={getText(
            locId,
            'PersonalInfo',
            'retAgeTooltip',
            'PersonalInfoForm'
          )}
          buttonText={getText(
            locId,
            'PersonalInfo',
            'retAgeModalButtonText',
            'PersonalInfoForm'
          )}
          displayModal={showModal}
          closeModalHandler={() => setShowModal(false)}
        />
        <div className="tooltip-wrapper">
          <NumberInput
            id="ret_age"
            data-testid="ret-age-input"
            hideLabel
            label="ret_age_label_hidden"
            name="ret_age"
            precision={0}
            rounded={false}
            value={localRetirementAge}
            onChange={handleRetAgeOnChange}
            onBlur={handleRetAgeOnBlur}
            error={retAgeError}
          />
          {!retAgeError ? (
            // eslint-disable-next-line react/jsx-one-expression-per-line
            <span className="tooltip-wrapper--info-message">
              {getText(
                locId,
                'PersonalInfo',
                'ageErrorSentence',
                'PersonalInfoForm'
              ).replace('%MIN_AGE%', retirementAgeRange.min.toString())}
              {getText(
                locId,
                'PersonalInfo',
                'ageErrorConjunction',
                'PersonalInfoForm'
              ).replace('%MAX_AGE%', retirementAgeRange.max.toString())}
            </span>
          ) : (
            <span className="tooltip-wrapper--info-message">{retAgeError}</span>
          )}
        </div>
        <div className="next-button">
          <Button
            label={getText(locId, 'AllPages', 'nextButton')}
            onClick={advance}
          />
        </div>
      </fieldset>
    </div>
  );
};

PersonalInfoForm.defaultProps = {
  ACTIONS: {
    SET_PERSONAL_INFO: 'setPersonalInfo',
    SET_HOUSEHOLD_INFO: 'setHouseholdInfo',
    SET_GOALS: 'setGoals',
  },
};

PersonalInfoForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  answersDispatch: PropTypes.func.isRequired,
  ACTIONS: PropTypes.objectOf(PropTypes.string),
  /* eslint-disable-next-line */
  answers: PropTypes.objectOf(PropTypes.any).isRequired,
  schema: PropTypes.shape({
    dependents: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    employment_status: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    hope: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    marital_status: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    worry: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
  }).isRequired,
  setQuizStarted: PropTypes.func.isRequired,
};

export default PersonalInfoForm;
